"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import {
  GripHorizontal as GripIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  MoveDiagonal2 as ResizeIcon,
  SquarePen as NewSessionIcon,
  X as XIcon,
} from "lucide-react";

import {
  OVERLAY_MIN_SIZE,
  useAssistantOverlay,
  type OverlayMessage,
} from "./useAssistantOverlay";

/**
 * Floating "blob" overlay — v0 sketch of the assistant surface.
 * Mirrors Figma node 792-7838 from the production UI file.
 *
 * Visual contract:
 * - Soft blurred dark backdrop (the "blob"); a warm glow appears on hover
 *   or while dragging, signalling that the surface is grabbable.
 * - Asymmetric message cards: user right (warmer fill), assistant left (recessive).
 * - Pill composer anchored at the bottom of the floating element.
 * - Message column fades on all four edges so older / newer messages
 *   cut off softly and the bubble edges feather into the blob.
 *
 * Interaction:
 * - The wrapper is `pointer-events: none`, so clicks on the underlying
 *   workspace (graph, list, etc.) pass through and the assistant stays
 *   put — it's a true float, not a modal.
 * - Drag from the message area or blob to reposition.
 * - Mouse wheel / trackpad scrolls the message column.
 * - Composer is non-drag (so click and selection work).
 * - Esc or the subtle X close button dismisses.
 *
 * No backend wiring. The send action loops a placeholder reply locally.
 */

// Soft fade on all four edges of the message column so older / newer
// messages cut off softly and the side edges feather over the bubble
// outlines a touch. Two linear gradients composited with intersect.
const MESSAGE_COLUMN_MASK =
  "linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%), linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)";
const MESSAGE_COLUMN_MASK_COMPOSITE = "intersect";
const MESSAGE_COLUMN_MASK_COMPOSITE_WEBKIT = "source-in";

// Portal gate for useSyncExternalStore: never fires, snapshots are
// constant — false on the server and during the hydration render, true
// afterwards.
const subscribeNever = () => () => {};
const getMountedSnapshot = () => true;
const getServerSnapshot = () => false;

type DragState = {
  startX: number;
  startY: number;
  baseX: number;
  baseY: number;
};

type ResizeState = {
  startX: number;
  startY: number;
  baseW: number;
  baseH: number;
  baseDragX: number;
  baseDragY: number;
};

export function AssistantOverlay() {
  const open = useAssistantOverlay((s) => s.open);
  const messages = useAssistantOverlay((s) => s.messages);
  const draft = useAssistantOverlay((s) => s.draft);
  const close_ = useAssistantOverlay((s) => s.close_);
  const setDraft = useAssistantOverlay((s) => s.setDraft);
  const send = useAssistantOverlay((s) => s.send);
  const dragOffset = useAssistantOverlay((s) => s.dragOffset);
  const setDragOffset = useAssistantOverlay((s) => s.setDragOffset);
  const size = useAssistantOverlay((s) => s.size);
  const setSize = useAssistantOverlay((s) => s.setSize);
  const newSession = useAssistantOverlay((s) => s.newSession);
  const voiceStatus = useAssistantOverlay((s) => s.voiceStatus);
  const toggleVoice = useAssistantOverlay((s) => s.toggleVoice);
  const isVoiceActive = voiceStatus === "active";
  const status = useAssistantOverlay((s) => s.status);
  const isThinking = status.kind === "thinking" || status.kind === "slow_path";

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const resizeRef = useRef<ResizeState | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const showGlow = isHovered || isDragging || isResizing;

  // Portal gate: the server renders nothing, so the first client render
  // must too — mounting the portal during hydration inserts a node into
  // document.body that the server HTML lacks and React flags a mismatch.
  const mounted = useSyncExternalStore(subscribeNever, getMountedSnapshot, getServerSnapshot);

  // Focus the composer whenever we open.
  useEffect(() => {
    if (open) {
      const t = window.setTimeout(() => inputRef.current?.focus(), 80);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // Auto-scroll to the latest message on open, new message, or session
  // reset. Defer to the next animation frame so the dialog has finished
  // its open transition and the messages column has its real height
  // before we anchor the bottom — without this, the first scroll on
  // open occasionally lands a few rows short.
  useEffect(() => {
    if (!open) return;
    const id = window.requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ block: "end" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [open, messages]);

  if (!mounted) return null;

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  const onHandlePointerDown: React.PointerEventHandler<HTMLButtonElement> = (event) => {
    if (event.button !== 0) return;
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      baseX: dragOffset.x,
      baseY: dragOffset.y,
    };
    setIsDragging(true);
    try {
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    } catch {
      // Pointer capture occasionally fails on synthetic events; safe to ignore.
    }
  };

  const onHandlePointerMove: React.PointerEventHandler<HTMLButtonElement> = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    setDragOffset({
      x: drag.baseX + (event.clientX - drag.startX),
      y: drag.baseY + (event.clientY - drag.startY),
    });
  };

  const endHandleDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setIsDragging(false);
    try {
      (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
    } catch {
      // Releasing a pointer we never captured throws; safe to ignore.
    }
  };

  const onResizePointerDown: React.PointerEventHandler<HTMLButtonElement> = (event) => {
    if (event.button !== 0) return;
    resizeRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      baseW: size.w,
      baseH: size.h,
      baseDragX: dragOffset.x,
      baseDragY: dragOffset.y,
    };
    setIsResizing(true);
    try {
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    } catch {
      // Pointer capture occasionally fails on synthetic events; safe to ignore.
    }
  };

  const onResizePointerMove: React.PointerEventHandler<HTMLButtonElement> = (event) => {
    const resize = resizeRef.current;
    if (!resize) return;
    const maxW = Math.floor(window.innerWidth * 0.9);
    const maxH = Math.floor(window.innerHeight * 0.9);
    const w = Math.min(Math.max(resize.baseW + (event.clientX - resize.startX), OVERLAY_MIN_SIZE.w), maxW);
    const h = Math.min(Math.max(resize.baseH + (event.clientY - resize.startY), OVERLAY_MIN_SIZE.h), maxH);
    // Compensate for the wrapper's `items-end justify-center` anchoring
    // so that grabbing the SE handle visually grows the right and bottom
    // edges (top-left stays put). Without this, the dialog grows
    // symmetrically around its center — width spreads both ways and
    // height pushes the top up — which feels like the handle is
    // resisting the drag.
    const dw = w - resize.baseW;
    const dh = h - resize.baseH;
    setSize({ w, h });
    setDragOffset({
      x: resize.baseDragX + dw / 2,
      y: resize.baseDragY + dh,
    });
  };

  const endResize = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!resizeRef.current) return;
    resizeRef.current = null;
    setIsResizing(false);
    try {
      (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
    } catch {
      // Releasing a pointer we never captured throws; safe to ignore.
    }
  };

  return createPortal(
    <div
      aria-hidden={!open}
      className={
        "pointer-events-none fixed inset-0 z-[80] flex items-end justify-center px-[var(--space-lg)] pb-[var(--space-xxl)] transition-opacity duration-200 ease-out " +
        (open ? "opacity-100" : "opacity-0")
      }
      data-overlay="assistant"
    >
      <div
        ref={dialogRef}
        className={
          "relative max-w-full transition-[opacity,transform] duration-200 ease-out " +
          (open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0")
        }
        style={{
          width: `${size.w}px`,
          height: `${size.h}px`,
          transform: `translate(${dragOffset.x}px, ${dragOffset.y}px) scale(${open ? 1 : 0.98})`,
        }}
        role="dialog"
        aria-label="Assistant"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Soft blurred "blob" backdrop. Sits behind the content; absorbs no clicks.
            A subtle warm glow appears when the dialog is hovered or being dragged
            so the user knows they can grab it. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 rounded-[84px] blur-[30px] transition-[box-shadow,background] duration-200 ease-out"
          style={{
            background: "var(--background)",
            boxShadow: showGlow
              ? "0 0 64px 8px color-mix(in srgb, var(--primary) 8%, transparent)"
              : "none",
          }}
        />

        {/* Drag handle — top-center, mirrors the container-control IconButton
            pattern (lucide GripHorizontal + grab cursor). Owns the drag so
            text selection in message bubbles works normally. */}
        <button
          type="button"
          aria-label="Drag assistant"
          className="absolute left-1/2 top-[var(--space-sm)] z-10 flex h-6 w-10 -translate-x-1/2 items-center justify-center rounded-full transition-colors"
          style={{
            color: isHovered || isDragging ? "var(--foreground)" : "var(--muted-foreground)",
            cursor: isDragging ? "grabbing" : "grab",
          }}
          onPointerDown={onHandlePointerDown}
          onPointerMove={onHandlePointerMove}
          onPointerUp={endHandleDrag}
          onPointerCancel={endHandleDrag}
        >
          <GripIcon size={16} aria-hidden />
        </button>

        {/* Resize handle — bottom-right corner. Lucide MoveDiagonal2:
            two arrows pointing NW and SE. Deliberately distinct from
            the textarea native resize indicator (three parallel slashes)
            so the surface affordance doesn't read as "resize the
            composer". Drag to resize the overlay; bounded by min size
            constants and 90% of viewport. */}
        <button
          type="button"
          aria-label="Resize assistant"
          className="absolute bottom-[var(--space-xs)] right-[var(--space-xs)] z-10 flex h-5 w-5 items-center justify-center rounded-[var(--radius-default)] transition-colors"
          style={{
            color: isResizing ? "var(--foreground)" : "var(--muted-foreground)",
            cursor: "nwse-resize",
          }}
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={endResize}
          onPointerCancel={endResize}
          onMouseEnter={(event) => {
            event.currentTarget.style.color = "var(--foreground)";
          }}
          onMouseLeave={(event) => {
            if (!isResizing) {
              event.currentTarget.style.color = "var(--muted-foreground)";
            }
          }}
        >
          <ResizeIcon size={14} aria-hidden />
        </button>

        {/* New-session button — top-left of the dialog. Wipes the
            visible thread and drops the live session id; the provider
            re-bootstraps a fresh backend session on the next render. */}
        <button
          type="button"
          onClick={newSession}
          aria-label="New session"
          className="absolute left-[var(--space-md)] top-[var(--space-md)] z-10 flex h-6 w-6 items-center justify-center rounded-full transition-colors"
          style={{
            color: "var(--muted-foreground)",
            cursor: "pointer",
          }}
          onMouseEnter={(event) => {
            event.currentTarget.style.color = "var(--foreground)";
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.color = "var(--muted-foreground)";
          }}
        >
          <NewSessionIcon size={14} aria-hidden />
        </button>

        {/* Subtle close button — top-right of the dialog. */}
        <button
          type="button"
          onClick={close_}
          aria-label="Close assistant"
          className="absolute right-[var(--space-md)] top-[var(--space-md)] z-10 flex h-6 w-6 items-center justify-center rounded-full transition-colors"
          style={{
            color: "var(--muted-foreground)",
            cursor: "pointer",
          }}
          onMouseEnter={(event) => {
            event.currentTarget.style.color = "var(--foreground)";
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.color = "var(--muted-foreground)";
          }}
        >
          <XIcon size={14} aria-hidden />
        </button>

        {/* Conversation column — fills the dialog so resize works. */}
        <div className="flex h-full min-h-0 flex-col gap-[var(--space-md)] px-[var(--space-lg)] pb-[var(--space-lg)] pt-[var(--space-xxl)]">
          <div
            className="flex min-h-0 flex-1 flex-col gap-[var(--space-md)] overflow-y-auto pr-[2px]"
            style={{
              maskImage: MESSAGE_COLUMN_MASK,
              WebkitMaskImage: MESSAGE_COLUMN_MASK,
              maskComposite: MESSAGE_COLUMN_MASK_COMPOSITE,
              WebkitMaskComposite: MESSAGE_COLUMN_MASK_COMPOSITE_WEBKIT,
              // Reserve a little padding inside the mask so the first/last
              // messages and the bubble edges have something to fade against.
              paddingTop: "var(--space-md)",
              paddingBottom: "var(--space-md)",
              paddingLeft: "var(--space-sm)",
              paddingRight: "var(--space-sm)",
            }}
          >
            {messages.map((m) => (
              <MessageCard key={m.id} message={m} />
            ))}
            {isThinking ? <ThinkingIndicator slow={status.kind === "slow_path"} /> : null}
            <div ref={messagesEndRef} />
          </div>

          {/* Composer — pill with textarea + mic toggle. The mic affordance
              is intentionally quiet (low contrast by default) per the
              modality contract: voice is a peer, not advertised as the
              primary path. Click toggles voice state; the future voice
              session plugs the actual mic capture / STT into this same
              start/end lifecycle. */}
          <div
            className="flex items-center gap-[var(--space-sm)] rounded-full border px-[var(--space-lg)] py-[var(--space-sm)] transition-colors"
            style={{
              background: "var(--background)",
              borderColor: isVoiceActive
                ? "color-mix(in srgb, var(--primary) 30%, var(--border))"
                : "var(--border)",
              boxShadow: "var(--shadow-sm)",
              cursor: "text",
            }}
          >
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isVoiceActive ? "Listening…" : "Ask…"}
              rows={1}
              disabled={isVoiceActive}
              className="w-full resize-none bg-transparent text-[14px] leading-[18px] outline-none placeholder:text-[color:var(--muted-foreground)] disabled:cursor-default"
              style={{ color: "var(--foreground)" }}
              aria-label="Message the assistant"
            />
            <button
              type="button"
              onClick={toggleVoice}
              aria-label={isVoiceActive ? "Stop voice input" : "Start voice input"}
              aria-pressed={isVoiceActive}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors"
              style={{
                color: isVoiceActive ? "var(--primary)" : "var(--muted-foreground)",
                cursor: "pointer",
              }}
              onMouseEnter={(event) => {
                if (!isVoiceActive) {
                  event.currentTarget.style.color = "var(--foreground)";
                }
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.color = isVoiceActive
                  ? "var(--primary)"
                  : "var(--muted-foreground)";
              }}
            >
              {isVoiceActive ? (
                <MicOffIcon size={14} aria-hidden />
              ) : (
                <MicIcon size={14} aria-hidden />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Quiet thinking indicator — three pulsing dots in an assistant-shaped
 * bubble. Per personality: no padding, no ceremony. When the backend
 * flips to slow_path (~15s), append a small "still thinking" hint so
 * the user knows the assistant didn't drop the ball.
 */
function ThinkingIndicator({ slow }: { slow: boolean }) {
  return (
    <div className="flex w-full justify-start">
      <div
        className="flex flex-col gap-[var(--space-xs)] rounded-[var(--radius-xxl)] p-[var(--space-md)]"
        style={{ background: "var(--card)" }}
      >
        <span className="flex items-center gap-[6px]" aria-label="Assistant thinking">
          <Dot delay={0} />
          <Dot delay={160} />
          <Dot delay={320} />
        </span>
        {slow ? (
          <span
            className="text-[12px] leading-[16px]"
            style={{ color: "var(--muted-foreground)" }}
          >
            still thinking…
          </span>
        ) : null}
      </div>
    </div>
  );
}

function Dot({ delay }: { delay: number }) {
  return (
    <span
      aria-hidden
      className="inline-block h-[6px] w-[6px] animate-pulse rounded-full"
      style={{
        background: "var(--muted-foreground)",
        animationDelay: `${delay}ms`,
      }}
    />
  );
}

function MessageCard({ message }: { message: OverlayMessage }) {
  const isUser = message.role === "user";
  return (
    <div className={"flex w-full " + (isUser ? "justify-end" : "justify-start")}>
      <div
        className="max-w-[80%] rounded-[var(--radius-xxl)] p-[var(--space-md)]"
        style={{
          background: isUser
            ? "var(--token-foundation-surface-pop)"
            : "var(--card)",
        }}
      >
        <p
          className={
            "text-[14px] leading-[18px] " + (isUser ? "text-right" : "text-left")
          }
          style={{ color: "var(--foreground)" }}
        >
          {message.text}
        </p>
      </div>
    </div>
  );
}
